import { atomFamily } from "jotai-family";
import { atomWithDefault } from "jotai/utils";
import { isEqual } from "lodash-es";
import type { FontSelectionSummary } from "../../brand-kit/hooks/use-font-selections";
import type { BrandTone } from "../../brand-kit/server/brand-tone-service";
import { brandInfoAtom, brandingAtom } from "../../brand-kit/store";
import { brandInfoToCustomizationsNew } from "../../design-huddle/brand-info-customizations/brand-info-customizations";
import {
  debugLabel,
  multiformatIconsFamily,
  multiformatPhotosFamily,
  multiformatVideosFamily,
} from "../../generate/store";
import type { AccountContext } from "../../generic/hooks/use-account-context";
import { imageUrlOverrideFamily } from "./image-url-override-family";
import { layoutConfigurationFamily } from "./layout-configuration-family";
import type { ImageUsage } from "./previews-family";
import type { MultiformatImage } from "./types";

type VideoAsset = { type: "video"; asset_id: number; index: number; tag: string };
export type ImageUrl =
  | (Omit<MultiformatImage, "type"> & {
      type: Exclude<MultiformatImage["type"], "video">;
      url: string;
    })
  | VideoAsset;
export const previewFamily = atomFamily(
  ({
    formatId,
    answer,
    imgNum,
    useSelectedLogo = true,
    templateIndex,
    templateCode,
    dimensions,
    images,
    multipage,
    fontSelections,
    imageUsage,
    db,
    fileId,
    brandTones,
    tone,
    context,
  }: {
    formatId: string;
    answer: string;
    imgNum: number;
    useSelectedLogo?: boolean;
    templateIndex: number;
    templateCode: string;
    dimensions?: { width?: number; height?: number };
    images: MultiformatImage[];
    multipage: boolean;
    fontSelections: FontSelectionSummary[];
    imageUsage: ImageUsage;
    db?: string;
    fileId?: string;
    brandTones: BrandTone[];
    tone?: string;
    context?: AccountContext;
  }) =>
    debugLabel(
      `previewFamily(${imgNum})`,
      atomWithDefault(async (get) => {
        const layoutPromise = get(
          layoutConfigurationFamily({
            formatId,
            answer,
            imgNum,
            fileId,
            brandTones,
            tone,
            ...(context && { context }),
          }),
        );

        const hasPhotoImages = images.find((image) => image.type === "photo");
        const photosPromise = hasPhotoImages
          ? get(
              multiformatPhotosFamily({
                formatId,
                description: answer,
                industry: context?.industry ?? "Technology & IT",
                db,
                fileId,
              }),
            )
          : Promise.resolve(null);

        const iconsPromise = images.find((image) => image.type === "icon")
          ? get(multiformatIconsFamily({ formatId, description: answer, fileId }))
          : Promise.resolve(null);

        const videosPromise = images.find((image) => image.type === "video")
          ? get(multiformatVideosFamily({ formatId, description: answer, fileId }))
          : Promise.resolve(null);

        const [layoutConfig, photos, icons, videos] = await Promise.all([
          layoutPromise,
          photosPromise,
          iconsPromise,
          videosPromise,
        ]);
        if (!layoutConfig) return null;

        const brandInfo = get(brandInfoAtom);
        const branding = get(brandingAtom);

        // Check if there are override imageUrls from "Apply to All" functionality
        const overrideImageUrls = get(imageUrlOverrideFamily({ formatId, imgNum, answer, fileId }));

        const imageUrls: ImageUrl[] =
          overrideImageUrls ||
          images
            .map((image): ImageUrl | null => {
              const { type, index } = image;

              if (type === "photo") {
                if (photos) {
                  const orientedPhotos = photos.filter((p) => p.orientation === image.orientation);
                  const offset =
                    image.orientation === "landscape"
                      ? imageUsage.photo.landscape
                      : imageUsage.photo.portrait;
                  const indexedUrl = orientedPhotos?.[index + offset]?.url;
                  const fallbackUrl =
                    orientedPhotos.length > 0
                      ? orientedPhotos[Math.floor(Math.random() * orientedPhotos.length)]!.url
                      : undefined;
                  return { ...image, type: "photo", url: indexedUrl || fallbackUrl || "" };
                }
              }

              if (type === "icon" && icons && icons.length > 0) {
                const offset = imageUsage.icon;
                const indexedUrl = icons[index + offset]?.url;
                const fallbackUrl = icons[Math.floor(Math.random() * icons.length)]!.url;
                if (!indexedUrl && !fallbackUrl) return null;
                return { ...image, type: "icon", url: indexedUrl || fallbackUrl || "" };
              }

              if (type === "illustration" && layoutConfig.response.url) {
                return { ...image, type: "illustration", url: layoutConfig.response.url };
              }

              if (type === "video" && videos && videos[index]) {
                const offset = imageUsage.video;
                return {
                  ...image,
                  ...{ asset_id: videos[index + offset]!.dh_asset_id },
                } as VideoAsset;
              }

              return null;
            })
            .filter((imageUrl): imageUrl is ImageUrl => imageUrl !== null);

        const customizations = brandInfoToCustomizationsNew({
          brandInfoSelected: branding,
          brandInfo,
          useSelectedLogo,
          mergeTags: layoutConfig.response.merge_tags,
          imageUrls,
          fontSelections,
        });

        const pageNumber = multipage ? 1 : templateIndex;
        return {
          formatId,
          title: layoutConfig.response.title,
          template_code: templateCode,
          customizations,
          page_number: pageNumber,
          ...dimensions,
          contentGenerationResultId: layoutConfig.contentGenerationResultId,
        };
      }),
    ),
  isEqual,
);
